import _ from 'lodash'
import constants from 'scoreboard/components/constants'
const { NUMBER, STABILIZE_NUMBER, SCORED_TEXT, GOAL_RED_FLAG,
	SCORED_MIDDLE_4_COLOR, LAST_VALUE, GOAL_ONLY } = constants
import calendars from 'scoreboard/components/calendars'
import config from 'scoreboard/components/config'
import ScorecardNode from 'scoreboard/scorecards/scorecard-node'
import globals from 'scoreboard/components/globals'
import { hasExtraColorBelow, hasExtraColorAbove } from './has-extra-color-above-below'
import DataType, {
	CURRENCY,
	PERCENT
} from 'scoreboard/datasets/model/data-type'
import ThresholdColorsModel from './threshold-colors/threshold-colors-model'
import {DATASETS, INITIATIVES} from './sections'
import {GREEN_MEDIUM, namesToHex, RED_MEDIUM} from './colors'
import {getPrecisionOrDefault, round} from './format-value'
import {
	BUDGET_REMAINING,
	BUDGET_SPENT, BUDGET_VARIANCE, BUDGET_VARIANCE_PERCENT,
	DAYS_ELAPSED,
	PERCENT_COMPLETE,
	PERCENT_TIME_ELAPSED, PROJECTED_BUDGET, SCHEDULE_VARIANCE
} from '../initiatives/initiative-calculation-type'
import moment from 'moment'
import { BLUE, SCORING } from './threshold-colors/gradients'
import moveDecimal from 'scoreboard/components/move-decimal'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
const { YES_NO } = DataType


/**
 * Determines whether higher is better.  Returns null if higher better couldn't
 * be determined based on the passed in data
 */
function higherBetter(opt) {
	// Identical logic in InitiativeImpactCalculator.java; if this function changes,
	// change InitiativeImpactCalculator too
	if (ScorecardNode.metricish(opt.model)) {
		let scoringType = config.scoringType(opt.model.scoringTypeId)
		if (_.get(scoringType, 'standardScoringType') == GOAL_ONLY) {
			return opt.model.higherBetter
		}
		// try to determine higher better based on current thresholds
		var thresholds = opt.currentScore && opt.currentScore.thresholds
		if (validThresholds(thresholds)) {
			return _.head(thresholds) < _.last(thresholds)
		}
		// try to determine based on previous thresholds
		thresholds = opt.previousScore && opt.previousScore.thresholds
		if (validThresholds(thresholds)) {
			return _.head(thresholds) < _.last(thresholds)
		}
		// we couldn't figure it out so return null
		return null
	}

	// for non-metrics, higher is always better
	return true
}

/**
 * Returns the thresholds array for scorecard nodes with a "scored text"
 * custom scoring type
 *
 * @param {ScoringType} scoringType
 * @return {Number[]}
 */
function scoredTextThresholds(scoringType) {
	// DUPLICATED IN ScoringBandsGenerator.addCustomTextBands()
	// IF CHANGED HERE, CHANGE THERE TOO
	let midpoints = []
	for (var i=0; i<scoringType.thresholds.length-1; i++) {
		let prev = scoringType.thresholds[i]
		let next = scoringType.thresholds[i+1]
		let mid = prev.score + (next.score - prev.score) / 2
		midpoints.push(mid)
	}
	let minimumScore =
		Math.min(_.first(scoringType.thresholds).score, config.minimumScore)
	let maximumScore =
		Math.max(_.last(scoringType.thresholds).score, config.maximumScore)
	return [minimumScore, ...midpoints, maximumScore]
}

/**
 * Returns a ThresholdColorsModel based on the passed in thresholdColoring or
 * based on the default thresholds if thresholdColoring is nil
 */
function getThresholdColorsModel(opt) {
	let thresholdColoring = opt.thresholdColoring
	if (!thresholdColoring) {
		let defaultThresholds = getDefaultThresholds(opt)
		let segments = _.isNil(defaultThresholds) ? 3 :
			_.size(defaultThresholds) - 1

		let reversedProperties = [ DAYS_ELAPSED, PERCENT_TIME_ELAPSED,
			BUDGET_REMAINING, PROJECTED_BUDGET]
		let reversed = reversedProperties.includes(opt.dataSourceProperty)

		let blueProperties = [BUDGET_SPENT, BUDGET_REMAINING]
		let gradient = blueProperties.includes(opt.dataSourceProperty) ?
			BLUE : SCORING

		thresholdColoring = {
			thresholds: defaultThresholds,
			segments,
			reversed,
			gradient
		}
	}
	return new ThresholdColorsModel(thresholdColoring)
}

/**
 * Rounds down a number to a specified precision, always choosing the next lower value.
 *
 * This function differs from standard rounding in that it always rounds down,
 * even when the digit to be rounded is 5 or higher. For example, 3.75 rounded
 * down to 1 decimal place would be 3.7, not 3.8. When rounding with precision 0,
 * it returns the largest integer less than or equal to the value.
 *
 * @param {number} value - The number to be rounded down.
 * @param {number} precision - The number of decimal places to round to.
 *                             If 0, rounds to the largest integer less than or equal to the value.
 * @returns {number} The value rounded down to the specified precision.
 *
 * @example
 * roundDown(3.14159, 2) // returns 3.13
 * roundDown(2.9, 0)     // returns 2
 * roundDown(2.75, 1)    // returns 2.7
 * roundDown(2.00, 2)    // returns 1.99
 * roundDown(14, 0)      // returns 13
 * roundDown(2.5, 0)     // returns 2
 */
function roundDown(value, precision) {
	if (precision === 0) {
		if (Number.isInteger(value)) {
			return value - 1
		} else {
			return Math.floor(value)
		}
	}
	const multiplier = 10 ** precision
	const roundedDown = Math.floor(value * multiplier) / multiplier
	const adjustment = 1 / multiplier
	return Number((roundedDown - adjustment).toFixed(precision))
}

/** Returns default thresholds for a gauge */
function getDefaultThresholds(opt) {
	if (opt.section === DATASETS) {
		let value = opt.datasetQueryResponse?.value
		if (_.isNil(value) || value === 0) {
			return scoreThresholds(3)
		} else {
			let precision = getPrecisionOrDefault(opt.precision)
			if (opt.dataType === PERCENT) {
				precision += 2
			}
			return [
				0,
				round(value * 0.5, precision),
				roundDown(value, precision),
				round(value * 1.5, precision)
			]
		}
	}
	if (opt.section === INITIATIVES) {
		return getInitDefaultThresholds(opt)
	}
}

function getInitDefaultThresholds(opt) {
	let budget = opt.domainObj.aggregatedBudget
	let startDate = opt.domainObj.aggregatedStartDate
	let endDate = opt.domainObj.aggregatedEndDate
	let scheduleRedThreshold = config.initiativesScheduleRedThreshold
	let budgetRedThreshold = config.initiativesBudgetRedThreshold

	let precision = getPrecisionOrDefault(opt.precision, opt.dataType)
	if (opt.dataType === PERCENT) {
		precision += 2
	}

	let days = null
	if (startDate && endDate) {
		days = moment(endDate).diff(startDate, 'days')
	}

	let values
	switch (opt.dataSourceProperty) {
		case PERCENT_COMPLETE:
		case PERCENT_TIME_ELAPSED:
			// 0, 33%, 67%, 100%
			return roundValues([0, 1/3, 2/3, 1], precision)
		case DAYS_ELAPSED:
			if (_.isNil(days) || days == 0) {
				return [0, 5, 10, 15]
			}
			values = [0, .5*days, days, 1.5*days]
			return roundValuesWithRoundDown(values, precision, days)
		// called "Projected Schedule Variance" in the UI
		case SCHEDULE_VARIANCE:
			if (_.isNil(days) || days == 0) {
				return [-5, 0, 5]
			}
			if (scheduleRedThreshold) {
				let diffOfDays = Math.round(scheduleRedThreshold * days)
				return roundValues([(-diffOfDays * 2), -diffOfDays, 0, diffOfDays], precision)
			}
			return roundValues([-.25*days, 0, .25*days], precision)
		// called "Money Spent to Date" in the UI
		case BUDGET_SPENT:
		case BUDGET_REMAINING:
			if (_.isNil(budget)) {
				return [0, 1000, 2000, 3000]
			}
			return roundValues([0, budget/3, 2/3*budget, budget], precision)
		// called "Projected Cost" in the UI
		case PROJECTED_BUDGET:
			if (_.isNil(budget)) {
				return [0, 1000, 2000]
			}
			if (budgetRedThreshold) {
				let monetaryAmount = budgetRedThreshold * budget
				values = [(budget - monetaryAmount), budget, (budget + monetaryAmount),
					budget + (monetaryAmount * 2)]
				return roundValuesWithRoundDown(values, precision, budget)
			}
			values = [.75*budget, budget, 1.25*budget]
			return roundValuesWithRoundDown(values, precision, budget)
		// called "Projected Budget Variance" in the UI
		case BUDGET_VARIANCE:
			if (_.isNil(budget)) {
				return [-1000, 0, 1000]
			}
			if (budgetRedThreshold) {
				let monetaryAmount = budgetRedThreshold * budget
				return roundValues([(-monetaryAmount * 2), -monetaryAmount, 0, monetaryAmount], precision)
			}
			return roundValues([-.25*budget, 0, .25*budget], precision)
		// called "Projected Budget Variance Percentage" in the UI
		case BUDGET_VARIANCE_PERCENT:
			if (budgetRedThreshold) {
				return [(-budgetRedThreshold * 2), -budgetRedThreshold, 0, budgetRedThreshold]
			}
			return [-.25, 0, .25]
	}
	throw Error('Unknown dataSourceProperty ' + opt.dataSourceProperty)
}

/**
 * Returns the given values rounded by the precision
 */
function roundValues(values, precision) {
	return _.map(values, value => round(value, precision))
}

/**
 * Rounds the given values to the specified precision, rounding down if
 * one of the values is equal to the roundDownValue
 * @param {Number[]} values the values to round
 * @param {Number} precision the precision to round to
 * @param {Number} roundDownValue the value to round down
 */
function roundValuesWithRoundDown(values, precision, roundDownValue) {
	return values.map(value => {
		if (roundDownValue == value) {
			return roundDown(value, precision)
		}
		return round(value, precision)
	})
}

/** Returns the numeric thresholds for the gauge */
function thresholds(opt) {
	var thresholds = []

	if ([DATASETS, INITIATIVES].includes(opt.section)) {
		if (opt.thresholdColoring) {
			return new ThresholdColorsModel(opt.thresholdColoring).computedThresholds()
		} else {
			return getDefaultThresholds(opt)
		}
	}

	let scoringType = config.scoringType(opt.model.scoringTypeId)
	if (_.get(scoringType, 'kpiValueType') == SCORED_TEXT) {
		if (calendars.isAggregatedPeriod(opt.model.calendarId, opt.periodId) &&
			opt.model.aggregationType != LAST_VALUE) {
			// we draw the speedometer just using the score
			return scoreThresholds(2)
		}
		return scoredTextThresholds(scoringType)
	}

	if (!ScorecardNode.metricish(opt.model)) {
		let colorOpt = colors(opt)
		let nonMetricScoringType = config.scoringType(config.nonMetricScoringTypeId)
		if (nonMetricScoringType.standardScoringType) {
			return scoreThresholds(colorOpt && colorOpt.length)
		} else {
			thresholds = _.map(nonMetricScoringType.thresholds, 'score')
			thresholds = withLoveHandles(thresholds, hasExtraColorBelow(nonMetricScoringType),
				hasExtraColorAbove(nonMetricScoringType))
			return thresholds
		}
	} else if (opt.model.dataType === YES_NO) {
		// handle % yes and # of yeses
		if (opt.currentScore && _.isNumber(opt.currentScore.actual)) {
			// we draw the speedometer just using the score
			thresholds = scoreThresholds(2)
			if (!opt.forceWorstFirst && !opt.model.yesGood) {
				thresholds = thresholds.reverse()
			}
		} else {
			// otherwise just return these placeholders
			thresholds = [ 0, 2, 4 ]
			if (opt.forceWorstFirst && !opt.model.yesGood) {
				thresholds = thresholds.reverse()
			}
		}
	} else {
		thresholds = opt.currentScore && opt.currentScore.thresholds
		if (!validThresholds(thresholds)) {
			return null
		}

		// add love handles if needed
		if (_.get(scoringType, 'standardScoringType') == GOAL_RED_FLAG ||
			_.get(scoringType, 'standardScoringType') == SCORED_MIDDLE_4_COLOR) {
			thresholds = withLoveHandles(thresholds, true, true)
		} else if (_.includes([NUMBER, STABILIZE_NUMBER], _.get(scoringType, 'kpiValueType'))) {
			thresholds = withLoveHandles(thresholds, hasExtraColorBelow(scoringType),
				hasExtraColorAbove(scoringType))
		}

		if (!opt.forceWorstFirst && _.head(thresholds) > _.last(thresholds)) {
			thresholds = _.clone(thresholds).reverse()
		}
	}

	return thresholds
}

/**
 * Returns the given thresholds but with love handles added onto the ends. Love
 * handles are extra colors on the ends of a scoring type. They are added on
 * both sides for certain standard scoring types (goal/base and 4 color scored
 * middle). For custom scoring types, they are added based on the values
 * for config.allowScoresBelowMin and config.allowScoresAboveMax. Note:
 * code is duplicated in BaseSpeedometerGenerator.java
 * @param {Number[]} thresholds the thresholds
 * @param {Boolean} belowMin whether to add love handles below the minimum
 * value of the thresholds
 * @param {Boolean} aboveMax whether to add love handles above the maximum
 * value of the thresholds
 * @return a copy of the thresholds array, but with love handles to possibly
 * be included
 */
function withLoveHandles(thresholds, belowMin, aboveMax) {
	let valueRange = _.last(thresholds) - _.head(thresholds)
	let handleSize = valueRange / (_.size(thresholds) - 1)

	let withLoveHandles = []
	if (belowMin) withLoveHandles.push(_.head(thresholds) - handleSize)
	withLoveHandles.push(...thresholds)
	if (aboveMax) withLoveHandles.push(_.last(thresholds) + handleSize)
	return withLoveHandles
}

/**
 * Returns thresholds based on the minimum and maximum scores and the number
 * of colors
 * @param numColors the number of colors
 */
function scoreThresholds(numColors) {
	var thresholds = []
	// generate fake thresholds for non-metrics
	let range = config.maximumScore - config.minimumScore
	for (var i=0; i<=numColors; i++) {
		thresholds.push(config.minimumScore + range * i / numColors)
	}
	return thresholds
}

/**
 * Determines whether the passed in thresholds are valid for drawing a
 * speedometer
 */
function validThresholds(thresholds) {
	if (_.isEmpty(thresholds)) {
		return false
	}

	// disallow null, undefined and NaN thresholds
	for (var i=0; i<thresholds.length; i++) {
		var threshold = thresholds[i]
		if (threshold == null || _.isUndefined(threshold) || isNaN(threshold)) {
			return false
		}
	}

	// verify thresholds are in a consistent order
	var ascending = thresholds[0] < thresholds[1]
	for (var i=1; i<thresholds.length-1; i++) {
		if (thresholds[i] < thresholds[i + 1] !== ascending) {
			return false
		}
	}
	return true
}

/**
 * Returns the colors array, e.g. [ RED_MEDIUM, YELLOW_MEDIUM, GREEN_MEDIUM ]
 */
function colors(opt) {
	if ([DATASETS, INITIATIVES].includes(opt.section)) {
		return getThresholdColorsModel(opt).getColors()
	}

	// handle yes/no
	if (opt.model.dataType === YES_NO) {
		return opt.model.yesGood || opt.forceWorstFirst ? [ RED_MEDIUM, GREEN_MEDIUM ] : [ GREEN_MEDIUM, RED_MEDIUM ]
	}
	// non-metrics
	if (!ScorecardNode.metricish(opt.model)) {
		return namesToHex(config.scoringType(config.nonMetricScoringTypeId).colors)
	}

	var scoringType = config.scoringType(opt.model.scoringTypeId)
	if (!scoringType) {
		return null
	}

	const scoringTypeColors = namesToHex(scoringType.colors)
	if (scoringType.kpiValueType == constants.SCORED_TEXT) {
		let periodId = opt.periodId || globals.periodId
		if (calendars.isAggregatedPeriod(opt.model.calendarId, periodId) &&
			opt.model.aggregationType != LAST_VALUE) {
			// #48078
			return [ RED_MEDIUM, GREEN_MEDIUM ]
		} else {
			return scoringTypeColors
		}
	}

	var thresholds = opt.currentScore && opt.currentScore.thresholds
	// if there are no thresholds we don't want colors on the speedometer other
	// than just gray
	if (!validThresholds(thresholds)) {
		return null
	}

	var higherBetter = _.get(scoringType, 'standardScoringType') == constants.GOAL_ONLY ?
		opt.model.higherBetter : _.head(thresholds) < _.last(thresholds)
	let colors = scoringTypeColors
	if (!opt.forceWorstFirst && !higherBetter) {
		colors = _.clone(colors).reverse()
	}
	return colors
}

/**
 * Returns the value to use for determining a color for threshold coloring.
 * Rounds the value to 7 decimal places and moves the decimal for percentages
 * to the hundredths place.
 */
function getValueForThresholdColoring(value, dataType) {
	if (_.isNil(value)) return value

	// #57920 if dataTypeForConditionalColoring is 'percent',
	// use moveDecimal so the user can enter a value between 0 and 100
	// for conditional coloring
	if (dataType === DataType.PERCENT) {
		value = moveDecimal(value, 2)
	}
	if (isNumericDataType(dataType)) {
		value = round(value, 7)
	}

	return value
}

export { validThresholds, thresholds, colors, higherBetter, roundDown,
	scoreThresholds, withLoveHandles, scoredTextThresholds,
	getThresholdColorsModel, getDefaultThresholds, getInitDefaultThresholds,
	getValueForThresholdColoring }
